import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../common/toast/hooks/useToast'
import { Payment, PaymentFormProps } from './Payments.d'
import { Card } from 'primereact/card'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import Common from '../../../common/Common'
import { Button, Alert, Row, Col } from 'react-bootstrap'
import { useAppSelector } from '../../../../store/hooks'
import PageHeader from '../../../layout/page/PageHeader'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import DeleteDialog from '../../../common/dialogs/DeleteDialog'
import SelectField from '../../../common/forms/fields/SelectField'
import TextField from '../../../common/forms/fields/TextField'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import dayjs from 'dayjs'
import ReadOnlyTextField from '../../../common/forms/fields/readonly/ReadOnlyTextField'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import RenderFormikErrors from '../../../common/forms/errors/RenderFormikErrors'

const PaymentForm: FC<PaymentFormProps> = ({
    id = null,
    inPopup = true,
    config = {},
}) => {
    const isViewOrEdit = id !== null
    const theme = useAppSelector((state) => state.theme.theme)

    const navigate = useNavigate()
    const axiosPrivate = useAxiosPrivate()

    const emptyPayment: Payment = {
        code: '',
        amount: null,
        payment_method_id: null,
        last_modified_at: null,
        status_id: null,
    }

    const PaymentFormSchema = Yup.object().shape({
        code: Yup.string().trim().required('Enter a unique payment code'),
        amount: Yup.string().trim().required('Enter an amount'),
        payment_method_id: Yup.string()
            .trim()
            .required('Select a payment method'),
        status_id: Yup.string().trim().required('Select payment status'),
    })

    const [paymentDialog, setPaymentDialog] = useState(true)
    const [payment, setPayment] = useState<Payment>(emptyPayment)
    const [paymentMethods, setPaymentMethods] = useState<[]>([])
    const [paymentStatuses, setPaymentStatuses] = useState<[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/finance/payments/' + id
            : '/finance/payments/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })
            if (response.data?.success === true) {
                if (response.data.data.payment) {
                    setPayment(response.data.data.payment)
                }

                setPaymentMethods(response.data.data.payment_methods || [])
                setPaymentStatuses(response.data.data.statuses || [])
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load payment')
            }
        }
    }, [axiosPrivate, id, isViewOrEdit])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record) => {
        setErrMsg('')
        setIsSubmitting(true)

        try {
            const routePath = isViewOrEdit
                ? '/finance/payments/' + id
                : '/finance/payments/create'
            const response = await axiosPrivate
                .post(routePath, record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            handleAxiosErrors(err, setErrMsg, setErrorFields, config)
        }
    }

    const hideDialog = () => {
        setPaymentDialog(false)
        navigate('/finance/payments')
    }

    const formFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            {isViewOrEdit && (
                <DeleteDialog
                    url={'/finance/payments/delete/' + id}
                    record={payment}
                    deletedCallback={() => hideDialog()}
                    btnLabel="Delete"
                    showIcon={false}
                    message={
                        <p className="mb-3 pb-3 border-bottom">
                            Are you sure you want to delete payment:
                            <strong> {payment.code}</strong>?
                        </p>
                    }
                />
            )}
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                // onClick={saveRecord}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'payment-form-submit-btn'
                    )
                    formSubmitGtn.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={{
                    ...payment,
                }}
                validationSchema={PaymentFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps) => {
                    return (
                        <FormikForm id="payment-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row>
                                <Col sm={12}>
                                    <fieldset>
                                        <legend>General</legend>
                                        <Row>
                                            <Col xl={6}>
                                                <TextField
                                                    id="formPaymentCode"
                                                    name="code"
                                                    label="Code"
                                                    placeholder="Enter payment code"
                                                    formikProps={formikProps}
                                                    readonly={isViewOrEdit}
                                                    required
                                                />
                                                <TextField
                                                    id="formPaymentAmount"
                                                    name="amount"
                                                    label="Amount"
                                                    placeholder="Enter payment amount"
                                                    formikProps={formikProps}
                                                    type="number"
                                                    required
                                                />
                                                {isViewOrEdit && (
                                                    <ReadOnlyTextField
                                                        id="formPaymentCreatedAt"
                                                        name="created_at"
                                                        label="Created at"
                                                        placeholder=""
                                                        value={
                                                            formikProps.values
                                                                .created_at
                                                                ? dayjs(
                                                                      formikProps
                                                                          .values
                                                                          .created_at
                                                                  ).format(
                                                                      'DD-MMM-YYYY @ HH:mm:ss'
                                                                  )
                                                                : ''
                                                        }
                                                        readonly={true}
                                                    />
                                                )}
                                            </Col>

                                            <Col xl={6}>
                                                <SelectField
                                                    id="formPaymentMethods"
                                                    name="payment_method_id"
                                                    label="Payment Method"
                                                    formikProps={formikProps}
                                                    choices={paymentMethods}
                                                    required
                                                />
                                                <SelectField
                                                    id="formPaymentStatus"
                                                    name="status_id"
                                                    label="Status"
                                                    formikProps={formikProps}
                                                    choices={paymentStatuses}
                                                    required
                                                />
                                                {isViewOrEdit && (
                                                    <ReadOnlyTextField
                                                        id="formPaymentModifiedAt"
                                                        name="last_modified_at"
                                                        label="Last modified"
                                                        placeholder=""
                                                        value={
                                                            formikProps.values
                                                                .last_modified_at
                                                                ? dayjs(
                                                                      formikProps
                                                                          .values
                                                                          .last_modified_at
                                                                  ).format(
                                                                      'DD-MMM-YYYY @ HH:mm:ss'
                                                                  )
                                                                : 'Has never been modified'
                                                        }
                                                        readonly={true}
                                                    />
                                                )}
                                            </Col>
                                        </Row>
                                    </fieldset>
                                </Col>
                            </Row>

                            <input
                                type="submit"
                                id="payment-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={paymentDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '65vw' }}
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : 'Edit payment ' + payment.code
                                : 'Create new payment'
                        }
                        modal
                        className="p-fluid"
                        footer={formFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : ''}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    <PageHeader
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : 'Edit payment ' + payment.code
                                : 'Create new payment'
                        }
                    />
                    {FormContent}
                    {formFooter}
                </>
            )}
        </>
    )
}

export default PaymentForm
